import React from 'react';
import { Avatar, ProviderBadge } from '@jotpsych/ui';
import { Box, Stack, Typography } from '@mui/material';

type IdentityProps = {
  primary?: React.ReactNode;
  secondary?: React.ReactNode;
};

type PatientIdentityProps = IdentityProps & {
  avatarSize?: 24 | 32;
  patientId?: string | null;
  patientName?: string | null;
};

type ProviderIdentityProps = IdentityProps & {
  badgeName?: string | null;
  /**
   * What this cell says when there is no name (GDN-3243). Defaults to the
   * app-wide "Unavailable", which reads as a fetch failure — correct where
   * a missing name IS unexpected. Surfaces where absence is EXPECTED (an
   * imported claim that never carried a provider) pass their own honest
   * wording. A string, not a node: a node prop would grow a second
   * rendering path for the same idea.
   */
  missingLabel?: string;
  providerId?: string | null;
  providerName?: string | null;
  size?: 'compact' | 'regular';
};

const splitIdentityName = (name?: string | null): { firstName: string; lastName: string } => {
  const parts = name?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};

const getProviderBadgeName = (name?: string | null): string | null =>
  name?.split(',')[0]?.trim() || null;

const identityText = (name?: string | null, missingLabel = 'Unavailable'): string =>
  name?.trim() || missingLabel;

export const PatientIdentity = ({
  avatarSize = 24,
  patientId,
  patientName,
  primary,
  secondary,
}: PatientIdentityProps) => {
  const displayName = identityText(patientName);

  return (
    <Stack
      data-patient-identity={patientId ?? patientName ?? 'unavailable'}
      direction="row"
      spacing={0.75}
      sx={{ alignItems: 'center', minWidth: 0 }}
    >
      <Avatar
        aria-hidden
        id={patientId ?? patientName ?? undefined}
        {...splitIdentityName(patientName)}
        sx={{
          flexShrink: 0,
          fontSize: avatarSize === 24 ? '0.625rem' : '0.75rem',
          fontWeight: 600,
          height: avatarSize,
          width: avatarSize,
        }}
      />
      <Stack spacing={0.15} sx={{ minWidth: 0 }}>
        {primary ?? (
          <Typography noWrap title={displayName} variant="body2">
            {displayName}
          </Typography>
        )}
        {secondary}
      </Stack>
    </Stack>
  );
};

export const ProviderIdentity = ({
  badgeName,
  missingLabel,
  providerId,
  providerName,
  primary,
  secondary,
  size = 'regular',
}: ProviderIdentityProps) => {
  const displayName = identityText(providerName, missingLabel);
  const canonicalName =
    badgeName === undefined ? getProviderBadgeName(providerName) : badgeName?.trim() || null;

  return (
    <Stack
      /* Unchanged on purpose: existing consumers query this by NAME.
         The id rides its own attribute rather than overloading this one
         into "sometimes a name, sometimes an id" (GDN-3243). */
      data-provider-identity={canonicalName ?? 'unassigned'}
      data-provider-id={providerId ?? undefined}
      direction="row"
      spacing={0.75}
      sx={{ alignItems: 'center', minWidth: 0 }}
    >
      <Box aria-hidden component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
        <ProviderBadge name={canonicalName} size={size} />
      </Box>
      <Stack spacing={0.15} sx={{ minWidth: 0 }}>
        {primary ?? (
          <Typography noWrap title={displayName} variant="body2">
            {displayName}
          </Typography>
        )}
        {secondary}
      </Stack>
    </Stack>
  );
};
